import { useState } from "react";
import { Calendar, Check, CheckCircle2, ChevronDown, ChevronUp, Clock, Copy, Rocket, ArrowRight, Target } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";

export interface DayPlan {
  day: number;
  title: string;
  keyAction: string;
  details: string;
  platformTips?: { platform: string; tip: string }[];
}

export interface ChecklistItem {
  label: string;
}

export interface PostLaunchItem {
  title: string;
  description: string;
}

export interface LaunchPlanData {
  days: DayPlan[];
  launchChecklist: ChecklistItem[];
  postLaunch: PostLaunchItem[];
}

function CopyBtn({ text, label = "Copy" }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button variant="ghost" size="sm" className="gap-1.5 h-8 px-2 text-muted-foreground hover:text-foreground" onClick={() => {
      navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Copied!");
      setTimeout(() => setCopied(false), 2000);
    }}>
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      <span className="text-xs">{copied ? "Copied" : label}</span>
    </Button>
  );
}

function getDate(dayOffset: number): string {
  const d = new Date();
  d.setDate(d.getDate() + dayOffset);
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
}

function getDayStatus(dayIndex: number): "done" | "today" | "upcoming" {
  // Day 0 = today
  if (dayIndex === 0) return "today";
  return "upcoming";
}

const dayColors = [
  "border-primary/40 bg-primary/10",
  "border-secondary/40 bg-secondary/10",
  "border-primary/40 bg-primary/10",
  "border-secondary/40 bg-secondary/10",
  "border-primary/40 bg-primary/10",
  "border-secondary/40 bg-secondary/10",
  "border-destructive/40 bg-destructive/10",
];

export default function LaunchAdvisorResults({ data }: { data: LaunchPlanData }) {
  const [completedDays, setCompletedDays] = useState<Set<number>>(new Set());
  const [completedChecklist, setCompletedChecklist] = useState<Set<number>>(new Set());
  const [expandedDays, setExpandedDays] = useState<Set<number>>(new Set([0]));

  const toggleDay = (i: number) => {
    setExpandedDays((prev) => {
      const n = new Set(prev);
      n.has(i) ? n.delete(i) : n.add(i);
      return n;
    });
  };

  const toggleComplete = (i: number) => {
    setCompletedDays((prev) => {
      const n = new Set(prev);
      n.has(i) ? n.delete(i) : n.add(i);
      return n;
    });
  };

  const toggleChecklist = (i: number) => {
    setCompletedChecklist((prev) => {
      const n = new Set(prev);
      n.has(i) ? n.delete(i) : n.add(i);
      return n;
    });
  };

  const fullText = data.days.map((d) => `Day ${d.day}: ${d.title}\n${d.keyAction}\n\n${d.details}`).join("\n\n---\n\n");

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-display font-bold text-foreground flex items-center gap-2">
          <Calendar className="w-5 h-5 text-destructive" />
          Your 7-Day Launch Plan
        </h2>
        <CopyBtn text={fullText} label="Copy Full Plan" />
      </div>

      {/* Progress */}
      <div className="space-y-2">
        <div className="flex items-center gap-1">
          {data.days.map((_, i) => (
            <div key={i} className={`flex-1 h-2 rounded-full transition-colors ${completedDays.has(i) ? "bg-primary" : "bg-border/30"}`} />
          ))}
        </div>
        <p className="text-xs text-muted-foreground text-center">{completedDays.size} of {data.days.length} days completed</p>
      </div>

      {/* Timeline */}
      <div className="relative space-y-0">
        {data.days.map((day, i) => {
          const colorClass = dayColors[i % dayColors.length];
          const isLast = i === data.days.length - 1;
          const isDone = completedDays.has(i);
          const isExpanded = expandedDays.has(i);
          const status = isDone ? "done" : getDayStatus(i);
          const dateLabel = getDate(i);

          return (
            <div key={i} className="relative flex gap-4">
              {/* Timeline connector */}
              <div className="flex flex-col items-center shrink-0">
                <button
                  onClick={() => toggleComplete(i)}
                  className={`w-12 h-12 rounded-xl border flex items-center justify-center z-10 transition-all ${
                    isDone ? "bg-primary border-primary text-primary-foreground" : colorClass
                  }`}
                >
                  {isDone ? (
                    <CheckCircle2 className="w-6 h-6" />
                  ) : (
                    <span className="font-display font-bold text-foreground text-lg">{day.day}</span>
                  )}
                </button>
                {!isLast && <div className="w-0.5 flex-1 bg-border/50 my-1" />}
              </div>

              {/* Card */}
              <div className="flex-1 pb-4">
                <Collapsible open={isExpanded} onOpenChange={() => toggleDay(i)}>
                  <div className="rounded-xl border border-border/50 bg-card/60 overflow-hidden">
                    <CollapsibleTrigger asChild>
                      <button className="w-full flex items-center justify-between px-5 py-3 border-b border-border/30 hover:bg-muted/30 transition-colors text-left">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Day {day.day}</span>
                            <span className="text-xs text-muted-foreground">·</span>
                            <span className="text-xs text-muted-foreground">{dateLabel}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium uppercase tracking-wider ${
                              status === "done" ? "bg-primary/20 text-primary" : status === "today" ? "bg-destructive/20 text-destructive" : "bg-muted text-muted-foreground"
                            }`}>
                              {status === "done" ? "Done" : status === "today" ? "Today" : "Upcoming"}
                            </span>
                          </div>
                          {day.title && <h3 className="font-display font-semibold text-foreground">{day.title}</h3>}
                          <p className="text-sm text-muted-foreground mt-0.5 truncate">{day.keyAction}</p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0 ml-3">
                          <CopyBtn text={`Day ${day.day}: ${day.title}\n${day.keyAction}\n\n${day.details}`} />
                          {isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                        </div>
                      </button>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="px-5 py-4 prose prose-invert prose-sm max-w-none">
                        <ReactMarkdown>{day.details}</ReactMarkdown>
                      </div>
                      {day.platformTips && day.platformTips.length > 0 && (
                        <div className="px-5 pb-4 space-y-2">
                          <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Platform-Specific Tips</h4>
                          <div className="grid gap-2">
                            {day.platformTips.map((tip, ti) => (
                              <div key={ti} className="flex items-start gap-2 p-2.5 rounded-lg bg-muted/30 border border-border/30">
                                <span className="text-xs font-medium text-primary shrink-0 mt-0.5">{tip.platform}</span>
                                <span className="text-sm text-muted-foreground">{tip.tip}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              </div>
            </div>
          );
        })}
      </div>

      {/* Launch Day Checklist */}
      {data.launchChecklist && data.launchChecklist.length > 0 && (
        <Card className="bg-card/50 border-destructive/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Rocket className="w-5 h-5 text-destructive" />
              Launch Day Checklist
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {data.launchChecklist.map((item, i) => {
              const done = completedChecklist.has(i);
              return (
                <button
                  key={i}
                  onClick={() => toggleChecklist(i)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left ${
                    done ? "border-primary/30 bg-primary/5" : "border-border/40 hover:border-primary/30"
                  }`}
                >
                  <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors ${
                    done ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground/40"
                  }`}>
                    {done && <Check className="w-3 h-3" />}
                  </div>
                  <span className={`text-sm ${done ? "text-muted-foreground line-through" : "text-foreground"}`}>{item.label}</span>
                </button>
              );
            })}
            <p className="text-xs text-muted-foreground text-center pt-2">
              {completedChecklist.size} of {data.launchChecklist.length} completed
            </p>
          </CardContent>
        </Card>
      )}

      {/* Post-Launch Section */}
      {data.postLaunch && data.postLaunch.length > 0 && (
        <Card className="bg-card/50 border-border/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Target className="w-5 h-5 text-primary" />
              Week 2: Post-Launch Strategy
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {data.postLaunch.map((item, i) => (
              <div key={i} className="p-4 rounded-lg border border-border/40 bg-muted/20">
                <div className="flex items-center gap-2 mb-1.5">
                  <ArrowRight className="w-4 h-4 text-primary shrink-0" />
                  <h4 className="font-medium text-foreground text-sm">{item.title}</h4>
                </div>
                <p className="text-sm text-muted-foreground ml-6">{item.description}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
